import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import { FaCheck } from 'react-icons/fa';
import { DATE_FORMAT_DEFAUT } from 'Config';
import Modal from 'common/components/modal/Modal';
import Button from 'common/components/button/Button';
import InputResizable from 'common/components/input/InputResizable';
import { ChargeValidationRow } from './models';
import { formatLineList, groupValidationRows } from './ChargeValidationService';
import ChargeValidationWarningList from './ChargeValidationWarningList';
import styles from './ChargeValidation.module.scss';

type Props = {
    isOpen: boolean;
    rows: ChargeValidationRow[];
    title: string;
    subtitle: string;
    confirmText: string;
    cancelText: string;
    /**
     * Cabeçalho por linha (nº, matrícula, data) e botão "Justificar" a abrir a caixa de texto — o modo
     * da importação. Na despesa manual há uma linha só e a caixa está sempre aberta.
     */
    showLineHeaders?: boolean;
    /** Justificações já gravadas, por linha (edição de uma despesa). */
    initialJustifications?: Record<number, string>;
    onCancel: () => void;
    onConfirm: (justificationsByLine: Record<number, string>) => void;
};

/**
 * Task #66220: um bloco por linha com os problemas listados por bullets e uma justificação própria —
 * antes era uma justificação só para todo o documento, que depois ficava duplicada em todas as
 * despesas e sem se saber a que linha dizia respeito. Linhas duplicadas entre si são justificadas em
 * conjunto (o aviso de duplicado traz as linhas irmãs).
 */
const ChargeValidationJustifyModal: React.FC<Props> = ({
    isOpen,
    rows,
    title,
    subtitle,
    confirmText,
    cancelText,
    showLineHeaders = true,
    initialJustifications,
    onCancel,
    onConfirm,
}: Props) => {
    const { t } = useTranslation();
    const groups = useMemo(() => groupValidationRows(rows), [rows]);

    const [justifications, setJustifications] = useState<Record<string, string>>({});
    const [expanded, setExpanded] = useState<Record<string, boolean>>({});

    useEffect(() => {
        if (!isOpen) return;
        const initial: Record<string, string> = {};
        groups.forEach(g => {
            const saved = g.lines.map(line => initialJustifications?.[line]).find(text => !!text?.trim());
            if (saved) initial[g.key] = saved;
        });
        setJustifications(initial);
        // Sem cabeçalhos por linha não há nada para abrir/fechar: a caixa está sempre visível.
        setExpanded(showLineHeaders ? Object.keys(initial).reduce<Record<string, boolean>>((acc, key) => ({ ...acc, [key]: true }), {}) : {});
    }, [isOpen]);

    const totalLines = rows.length;
    const justifiedLines = groups.filter(g => !!justifications[g.key]?.trim()).reduce((sum, g) => sum + g.lines.length, 0);
    const canConfirm = totalLines > 0 && justifiedLines === totalLines;

    const confirm = () => {
        const byLine: Record<number, string> = {};
        groups.forEach(g => {
            const text = justifications[g.key]?.trim();
            if (text) g.lines.forEach(line => { byLine[line] = text; });
        });
        onConfirm(byLine);
    };

    const renderGroupHeader = (lines: number[], row: ChargeValidationRow, key: string) => {
        const isExpanded = !!expanded[key];
        const isJustified = !!justifications[key]?.trim();
        const context = [row.registrationNumber, row.date ? moment(row.date).format(DATE_FORMAT_DEFAUT) : null].filter(v => !!v).join(' · ');

        return (
            <div className={styles.groupHeader}>
                <div className={styles.groupTitle}>
                    <span className={styles.groupLines}>
                        {t(lines.length > 1 ? 'import_charges.lines' : 'import_charges.line')} {formatLineList(lines, t)}
                    </span>
                    {context ? ` · ${context}` : ''}
                </div>
                <Button
                    preset={'secondary'}
                    size={'extraSmall'}
                    className={`${styles.justifyButton} ${isExpanded || isJustified ? '' : styles.justifyButtonIdle}`}
                    onClick={() => setExpanded(prev => ({ ...prev, [key]: !prev[key] }))}
                >
                    {isJustified ? <FaCheck /> : <></>}
                    {t('charges.validation.justify')}
                </Button>
            </div>
        );
    };

    return (
        <Modal
            isOpen={isOpen}
            onRequestClose={onCancel}
            ariaHideApp={false}
            style={{
                // O scroll é do corpo (cabeçalho e rodapé ficam fixos), por isso o conteúdo não rola.
                content: { position: 'relative', inset: 'unset', width: 640, maxWidth: '92vw', padding: 0, overflow: 'hidden' },
            }}
        >
            <div className={styles.modal}>
                <div className={styles.header}>
                    <h3 className={styles.title}>{title}</h3>
                    <p className={styles.subtitle}>{subtitle}</p>
                </div>

                <div className={styles.body}>
                    {groups.map(group => (
                        <div key={group.key} className={styles.group}>
                            {showLineHeaders ? renderGroupHeader(group.lines, group.rows[0], group.key) : <></>}

                            <ChargeValidationWarningList rows={group.rows} />

                            {(!showLineHeaders || expanded[group.key]) &&
                                <div className={styles.justification}>
                                    <InputResizable
                                        value={justifications[group.key] ?? ''}
                                        // O valor é lido já aqui: dentro do updater o evento sintético já
                                        // foi reciclado pelo React e o currentTarget é null.
                                        onChange={e => {
                                            const text = e.currentTarget.value;
                                            setJustifications(prev => ({ ...prev, [group.key]: text }));
                                        }}
                                        placeholder={t(group.lines.length > 1 ? 'charges.validation.justification_placeholder_lines' : 'charges.validation.justification_placeholder_line') as string}
                                        rows={2}
                                    />
                                </div>}
                        </div>
                    ))}
                </div>

                <div className={styles.footer}>
                    {showLineHeaders
                        ? <div className={styles.counter}>
                            <span className={styles.counterDone}>{justifiedLines}</span> {t('charges.validation.justified_counter', { total: totalLines })}
                        </div>
                        : <div />}
                    <div className={styles.actions}>
                        <Button preset={'secondary'} type="button" text={cancelText} onClick={onCancel} />
                        <Button type="button" text={confirmText} isDisabled={!canConfirm} onClick={confirm} />
                    </div>
                </div>
            </div>
        </Modal>
    );
};

export default ChargeValidationJustifyModal;
